'use client';

import { c as _c } from "react/compiler-runtime";
import { type EmblaCarouselType } from 'embla-carousel';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import { Button } from '@/app/components/button';
import { HeaderLevel2 } from '@/app/components/headers/header-level-2';
import { IconOpenInFull } from '@/app/components/icons/ui-actions/icon-open-in-full';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { Image } from '@/app/components/media/image';
import { useOverlayMountControl } from '@/app/components/overlays/hooks/useOverlayMountControl';
import { Sliders } from '@/app/components/sliders';
import { MODAL_KEYS } from '@/app/constants/overlayKeys';
import styles from '@/app/cv/components/sections/products/index.module.css';
import type { ProductGallerySectionProps } from '@/app/cv/components/sections/products/types';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
const PRODUCT_GALLERY_INSTANCE_ID = 'product-gallery';
const GalleryZoomModal = dynamic(() => import('@/app/components/overlays/modal/gallery-zoom-modal').then(mod => mod.GalleryZoomModal), {
  ssr: false
});
export const ProductGallery = t0 => {
  const $ = _c(37);
  const {
    items: t1,
    lead: t2,
    title: t3,
    testId: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const items = t5;
  const lead = t2 === undefined ? "Gallery" : t2;
  const title = t3 === undefined ? "\u0E41\u0E01\u0E25\u0E40\u0E25\u0E2D\u0E23\u0E35\u0E48" : t3;
  const testId = t4 === undefined ? "product-gallery" : t4;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      defaultBaseDelay: 0.04
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  useRevealAnim(t6);
  const [emblaApi, setEmblaApi] = useState(null);
  let t7;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [];
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const [activeSlideIndexes, setActiveSlideIndexes] = useState(t7);
  const [selectedIndex, setSelectedIndex] = useState(0);
  let t8;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      overlayKey: MODAL_KEYS.MODAL_GALLERY,
      instanceId: PRODUCT_GALLERY_INSTANCE_ID
    };
    $[4] = t8;
  } else {
    t8 = $[4];
  }
  const {
    shouldRender,
    open
  } = useOverlayMountControl(t8);
  let t9;
  if ($[5] !== items) {
    t9 = items.map(_temp);
    $[5] = items;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  const galleryImages = t9;
  let t10;
  if ($[7] !== open) {
    t10 = index_0 => {
      setSelectedIndex(index_0);
      open();
    };
    $[7] = open;
    $[8] = t10;
  } else {
    t10 = $[8];
  }
  const handleOpenModal = t10;
  let t11;
  let t12;
  if ($[9] !== emblaApi) {
    t11 = () => {
      if (!emblaApi) {
        return;
      }
      const updateSlidesInView = () => {
        setActiveSlideIndexes(emblaApi.slidesInView());
      };
      updateSlidesInView();
      emblaApi.on("slidesInView", updateSlidesInView);
      emblaApi.on("reInit", updateSlidesInView);
      return () => {
        emblaApi.off("slidesInView", updateSlidesInView);
        emblaApi.off("reInit", updateSlidesInView);
      };
    };
    t12 = [emblaApi];
    $[9] = emblaApi;
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  useEffect(t11, t12);
  const t13 = `${testId}-section`;
  let t14;
  if ($[12] !== t13) {
    t14 = createDataAttributes({
      test: t13
    });
    $[12] = t13;
    $[13] = t14;
  } else {
    t14 = $[13];
  }
  const t15 = !title;
  let t16;
  if ($[14] !== t15) {
    t16 = cn("mb-6 text-white sm:mb-10", {
      hidden: t15
    });
    $[14] = t15;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  let t17;
  if ($[16] !== lead || $[17] !== title) {
    t17 = <HeaderLevel2 theme="dark" lead={lead} title={title} leadClassName="text-white" content2ClassName="space-y-2" />;
    $[16] = lead;
    $[17] = title;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  let t18;
  if ($[19] !== t16 || $[20] !== t17) {
    t18 = <Container className={t16} data-revealanim={true}>{t17}</Container>;
    $[19] = t16;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] !== activeSlideIndexes || $[23] !== handleOpenModal || $[24] !== items) {
    t19 = items.length > 0 && <Sliders onEmblaInit={setEmblaApi} align="start" loop={true} emblaOptions={{
      inViewThreshold: 0.5
    }}>{items.map((item_0, index_1) => <div key={index_1} className={cn("group relative shrink-0 cursor-pointer", styles.gallerySlide)} onClick={() => handleOpenModal(index_1)} {...createDataAttributes({
        test: `gallery-item-${index_1}`,
        active: activeSlideIndexes.includes(index_1) ? true : undefined
      })}><div className="aspect-1-1 relative overflow-hidden bg-gray-800"><Image src={item_0} alt={`gallery-${index_1}`} width={360} height={360} aspectRatio="aspect-1-1" hoverEffect="zoom" isGroupHover={true} /><div className="pointer-events-none absolute top-6 right-6 z-10 scale-0 opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100 pointer-coarse:scale-100 pointer-coarse:opacity-100"><Button iconBefore={<IconOpenInFull />} color="white" {...createDataAttributes({
              test: "full-view-button"
            })} /></div></div></div>)}</Sliders>;
    $[22] = activeSlideIndexes;
    $[23] = handleOpenModal;
    $[24] = items;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== t14 || $[27] !== t18 || $[28] !== t19) {
    t20 = <Section id="gallery" size="lg" className="bg-linear-0 from-gray-800 from-0% to-gray-700 to-100%" data-revealanim-group={true} {...t14}>{t18}{t19}</Section>;
    $[26] = t14;
    $[27] = t18;
    $[28] = t19;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== galleryImages || $[31] !== selectedIndex || $[32] !== shouldRender) {
    t21 = !!shouldRender && <GalleryZoomModal images={galleryImages} initialIndex={selectedIndex} overlayKey={MODAL_KEYS.MODAL_GALLERY} instanceId={PRODUCT_GALLERY_INSTANCE_ID} testId="product-gallery" />;
    $[30] = galleryImages;
    $[31] = selectedIndex;
    $[32] = shouldRender;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t20 || $[35] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[34] = t20;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  return t22;
};
function _temp(item, index) {
  return {
    src: item,
    alt: `gallery-${index}`,
    width: 1600,
    height: 900
  };
}